import { useCallback, useEffect, useState } from 'react';
import { Anchor, Button, Card, Radio, Stack, Text, Title } from '@mantine/core';
import { api, errorMessage } from '../api/client';
import type { BillingInterval, BillingView } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import {
  ANNUAL_SAVING,
  BUYABLE_PLANS,
  CONTACT_EMAIL,
  count,
  intervalWords,
  isPaying,
  monthName,
  offerFor,
  periodWords,
  priceWords,
  statusWords,
} from '../billing';
import { leaveFor } from '../browser';
import { dayLabel } from '../format';
import { ErrorNote } from './ErrorNote';
import { Loading } from './Loading';
import { ReadEdit } from './ReadEdit';
import { ReadList, ReadRow } from './ReadValue';

const mailTo = `mailto:${CONTACT_EMAIL}`;

/**
 * What a publisher is on, and the only two things anybody can do about it from here.
 *
 * The state this is designed around first is the one it will be in most of the time: there is no
 * Stripe account behind quizee yet, so `configured` comes back false and will keep coming back
 * false for a while. That is not a fault and it is not an outage. A publisher on this screen with
 * billing switched off still gets the whole answer to "what are we on and what does it cover", and
 * the part they cannot do yet is written as what it is, a plan being something we set up with them
 * rather than something they buy on a screen. Nothing here is greyed out, nothing says "coming
 * soon", and no checkout is offered that could not work.
 *
 * Three rules the wording has to keep, all of them promises made in public on the pricing page:
 *
 * - The plan's allowance and any grant added for this month are two numbers and never one. A
 *   single pre-added total hides where the extra came from and hides that it goes away at the end
 *   of the month, which is exactly what somebody looking at this needs to know.
 * - Going past the allowance stops nothing. There is no cap, no cut off and no overage charge, so
 *   nothing here may imply one. A publisher having their best traffic day should not meet a
 *   sentence that reads like a meter running out.
 * - The Network plan is a conversation. It gets a contact link, never a button pretending to sell
 *   something whose price nobody has agreed yet.
 *
 * Card numbers do not exist in this app. Checkout and the portal are pages Stripe hosts, and this
 * screen's whole job on that side is to send somebody to the right one.
 *
 * Read first, like every other settings section: the plan is what the screen says, and the choice
 * of a new one appears only when an owner presses the button. Somebody who cannot manage billing
 * is not offered the button at all rather than being refused after pressing it.
 */
export function PlanSection() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';

  const [billing, setBilling] = useState<BillingView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setProblem(null);
    setBilling(null);
    api
      .billing(orgId)
      .then(setBilling)
      .catch((error: unknown) => setProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  return (
    <Card component="section" withBorder radius="lg" p="lg">
      <Title order={2} size="h3">
        Your plan
      </Title>

      {problem !== null && <ErrorNote message={problem} onRetry={load} />}
      {problem === null && billing === null && <Loading label="Loading your plan" />}
      {billing !== null && orgId !== null && (
        <Plan billing={billing} orgId={orgId} orgName={orgName} />
      )}
    </Card>
  );
}

/** What the plan chooser holds while it is open. A plan and how it is paid for, nothing else. */
interface CheckoutDraft {
  readonly plan: string;
  readonly interval: BillingInterval;
}

function Plan({
  billing,
  orgId,
  orgName,
}: {
  billing: BillingView;
  orgId: string;
  orgName: string;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const offer = offerFor(billing.plan);
  const label = offer?.label ?? billing.plan_label;
  const onNetwork = billing.plan === 'network';

  /**
   * Three questions, and they are not the same question.
   *
   * `paying` is whether money is moving now, which is what a second checkout must never be offered
   * beside: that is how somebody ends up with two subscriptions. `hasBought` is whether Stripe
   * holds anything for this publisher at all, which is what makes the portal worth offering, and a
   * publisher who cancelled still has invoices to fetch. A cancelled or an abandoned checkout is
   * the case where both are true at once, and that publisher is rightly offered both.
   */
  const paying = isPaying(billing.status);
  const hasBought = billing.status !== 'none';
  const canBuy = billing.configured && billing.can_manage && !paying && !onNetwork;
  const canPortal = billing.configured && billing.can_manage && hasBought;

  async function startCheckout(draft: CheckoutDraft): Promise<boolean> {
    setProblem(null);
    try {
      const link = await api.startCheckout(orgId, { plan: draft.plan, interval: draft.interval });
      leaveFor(link.url);
      // The browser is on its way to Stripe, so the form stays as it was rather than flashing back
      // to the read view underneath a page that is leaving.
      return false;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  async function openPortal(): Promise<void> {
    setProblem(null);
    setPortalBusy(true);
    try {
      const link = await api.billingPortal(orgId);
      leaveFor(link.url);
    } catch (error) {
      setProblem(errorMessage(error));
      setPortalBusy(false);
    }
  }

  return (
    <>
      {problem !== null && <ErrorNote message={problem} />}

      <ReadEdit<CheckoutDraft>
        noun="your plan"
        canEdit={canBuy}
        openLabel="Change plan"
        openName="Change plan"
        saveLabel="Continue to payment"
        saveName="Continue to payment"
        busyLabel="Taking you to Stripe..."
        open={() => ({
          plan: BUYABLE_PLANS[0]?.plan ?? 'single',
          interval: billing.interval,
        })}
        form={(draft, set) => <PlanChoice draft={draft} set={set} />}
        save={startCheckout}
      >
        <PlanReadout billing={billing} label={label} hasBought={hasBought} />
      </ReadEdit>

      {!billing.configured && <NotConfigured canManage={billing.can_manage} orgName={orgName} />}

      {billing.configured && !billing.can_manage && (
        <Text mt="md">
          The plan is looked after by the owners of {orgName}. Everything above is the whole
          picture: you can read what the plan covers and what has been used, and changing it, along
          with anything to do with payment, is theirs to do.
        </Text>
      )}

      {billing.configured && billing.can_manage && onNetwork && (
        <Text mt="md">
          The Network plan is the one we agreed with you rather than one bought from a page, so
          there is nothing to buy here. Email <Anchor href={mailTo}>{CONTACT_EMAIL}</Anchor> and we
          will change it with you.
        </Text>
      )}

      {canPortal && <Portal busy={portalBusy} onOpen={() => void openPortal()} />}
    </>
  );
}

/** The plan as it stands, written to be read. Everything here is a fact, nothing is a control. */
function PlanReadout({
  billing,
  label,
  hasBought,
}: {
  billing: BillingView;
  label: string;
  hasBought: boolean;
}) {
  const offer = offerFor(billing.plan);
  // Before anybody has bought anything, the price to quote is the monthly one, because that is
  // what the pricing page leads with and no interval has been chosen yet.
  const price =
    offer === undefined ? null : priceWords(offer, hasBought ? billing.interval : 'monthly');
  const grantMonth = monthName(billing.granted_month) ?? 'this month';
  const granted = billing.granted !== null && billing.granted > 0 ? billing.granted : null;

  return (
    <ReadList>
      <ReadRow label="Plan" value={label} note={offer?.brands} />

      {price !== null && <ReadRow label="Price" value={price} />}

      {hasBought && (
        <ReadRow
          label="Subscription"
          value={statusWords(billing.status)}
          note={
            billing.current_period_end === null
              ? undefined
              : periodWords(billing.status, dayLabel(billing.current_period_end))
          }
        />
      )}

      {hasBought && (
        <ReadRow
          label="Billed"
          value={intervalWords(billing.interval)}
          note={
            billing.interval === 'annual'
              ? ANNUAL_SAVING
              : 'Month to month, and it stops at the end of a month you have paid for.'
          }
        />
      )}

      <ReadRow
        label="Responses on your plan"
        value={
          billing.allowance === null ? 'Agreed with you' : `${count(billing.allowance)} a month`
        }
        note={
          billing.allowance === null
            ? 'The number on this plan is the one we agreed with you rather than a published figure.'
            : 'This is what puts you on a plan rather than a limit that stops anything. Going past it in a busy month costs nothing extra and switches nothing off.'
        }
      />

      {granted !== null && (
        <ReadRow
          label={`Added for ${grantMonth}`}
          value={count(granted)}
          note={`On top of what the plan includes, for ${grantMonth} only. It is not part of the plan and it does not carry into next month.`}
        />
      )}
    </ReadList>
  );
}

/**
 * What we say while there is no payment provider behind any of this.
 *
 * Written as an ordinary fact about how plans are handled today rather than as a thing that is
 * missing, because from a publisher's side it is not missing: a plan is agreed in an email and set
 * up by us, which is how every plan on quizee has been set up so far.
 */
function NotConfigured({ canManage, orgName }: { canManage: boolean; orgName: string }) {
  return (
    <Text mt="md">
      Paying by card is not switched on here yet, so plans are set up by talking to us rather than
      by clicking, and nothing on this screen can change what you pay.{' '}
      {canManage ? (
        <>
          Email <Anchor href={mailTo}>{CONTACT_EMAIL}</Anchor> and we will sort it out. Nothing
          changes on your account until you have agreed it.
        </>
      ) : (
        <>The owners of {orgName} are the people who arrange it with us.</>
      )}
    </Text>
  );
}

/** The way out to Stripe, for a publisher who already has a subscription. */
function Portal({ busy, onOpen }: { busy: boolean; onOpen: () => void }) {
  return (
    <>
      <Text mt="md">
        Your card, your invoices and cancelling all live in Stripe's own portal. We never see a card
        number and never ask for one, so this takes you there rather than showing it here. To move
        to another plan, email <Anchor href={mailTo}>{CONTACT_EMAIL}</Anchor> and we will work out
        what fits.
      </Text>
      <Button variant="default" size="compact-sm" mt="sm" disabled={busy} onClick={onOpen}>
        {busy ? 'Taking you to Stripe...' : 'Manage payment and invoices'}
      </Button>
    </>
  );
}

/**
 * Choosing a plan: how it is paid for, then which one.
 *
 * The interval comes first because it changes every price underneath it, so the prices somebody is
 * comparing are the ones they would actually pay. The saving is the pricing page's own sentence,
 * word for word: a publisher who read that page should not have to work out whether this is the
 * same offer.
 */
function PlanChoice({ draft, set }: { draft: CheckoutDraft; set: (next: CheckoutDraft) => void }) {
  return (
    <Stack gap="lg">
      <Radio.Group
        label="How you pay"
        description={ANNUAL_SAVING}
        value={draft.interval}
        onChange={(value) => set({ ...draft, interval: value === 'annual' ? 'annual' : 'monthly' })}
      >
        <Stack gap="xs" mt="xs">
          <Radio value="monthly" label="Monthly" />
          <Radio value="annual" label="Annual" />
        </Stack>
      </Radio.Group>

      <Radio.Group
        label="Plan"
        description="Every plan has every format, every seat and the same access to your own responses. What changes is how many brands you run it across and how much you use it."
        value={draft.plan}
        onChange={(value) => set({ ...draft, plan: value })}
      >
        <Stack gap="xs" mt="xs">
          {BUYABLE_PLANS.map((plan) => (
            <Radio
              key={plan.plan}
              value={plan.plan}
              label={`${plan.label}, ${priceWords(plan, draft.interval) ?? ''}`}
              description={`${plan.brands}, ${plan.responses}.`}
            />
          ))}
        </Stack>
      </Radio.Group>

      <Text size="sm" c="dimmed">
        More than eight titles is the Network plan, and that one is settled in a conversation rather
        than on a page: forty weeklies and forty daily sites are not the same amount of use. Email{' '}
        <Anchor href={mailTo} size="sm">
          {CONTACT_EMAIL}
        </Anchor>{' '}
        and tell us what you publish.
      </Text>

      <Text size="sm" c="dimmed">
        The card is typed on Stripe's own page and never here. Nothing is charged until you have
        finished there, and you can come back without buying anything.
      </Text>
    </Stack>
  );
}
